import { useEffect, useState, type RefObject } from "react";

/**
 * Ported from bouncieweb's `hooks/useHasBeenOnScreen` (built on `useOnScreen`).
 *
 * Turns "is this currently on screen?" into "has this ever been on screen?" so
 * scroll-triggered animations fire once, the first time an element scrolls into
 * view, and then stay in their revealed state. Uses the native
 * IntersectionObserver — no dependency on the old Redux/hook stack.
 */
export function useHasBeenOnScreen(ref: RefObject<Element | null>): boolean {
  const [hasBeenOnScreen, setHasBeenOnScreen] = useState(false);

  useEffect(() => {
    if (hasBeenOnScreen) return;
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setHasBeenOnScreen(true);
        observer.disconnect();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, hasBeenOnScreen]);

  return hasBeenOnScreen;
}
